import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Loader2 } from 'lucide-react'
import { useApi } from '../../utils/api'
import { useMessage } from '../../contexts/MessageContext'
import { Input } from '../../components/ui/input'
import { Button } from '../../components/ui/button'
import { PageTitleBar } from '../../components/PageTitleBar'
import { ListTable } from '../../components/ListTable'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog'

interface EmailTemplate {
  id: number;
  title: string;
  subject: string;
  created_at: string;
}

interface ApiResponse {
  data: {
    templates: EmailTemplate[];
  };
  success: boolean;
}

export default function EmailTemplatesList() {
  const { message, setMessage } = useMessage();
  const navigate = useNavigate();
  const { fetchWithAuth } = useApi();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const columns = [
    { header: 'Title', accessor: 'title' },
    { header: 'Subject', accessor: 'subject' },
    { header: 'Created At', accessor: 'created_at' },
  ];

  const handleEditTemplate = (id: number) => {
    navigate(`/edit-email-template/${id}`);
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteTemplate = (id: number) => {
    setTemplateToDelete(id);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setTemplateToDelete(null);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/admin/email-templates/${templateToDelete}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to delete template');
      }
      setTemplates(templates.filter(template => template.id !== templateToDelete));
      setMessage('Template successfully deleted!');
      closeDeleteModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth('/admin/email-templates');
      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }
      const data = await response.json();
      // Check if data is an array (direct response) or wrapped in a data object
      if (Array.isArray(data)) {
        setTemplates(data);
      } else if (data.data && Array.isArray(data.data.templates)) {
        setTemplates(data.data.templates);
      } else {
        console.error('Unexpected data format:', data);
        setTemplates([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filteredTemplates = templates.filter(template =>
    template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {message && (
        <div className="mb-4 p-4 bg-[#8bcfc2] border border-[#3eaf99] text-[#0b677c] rounded">
          {message}
        </div>
      )}

      <PageTitleBar
        title="Email Templates"
        breadcrumbs={[
          { label: 'Dashboard', href: '/' },
          { label: 'Email Templates' },
        ]}
        actionLabel="New Template"
        actionHref="/new-email-template"
      />

      <div className="bg-white rounded-sm shadow-md">
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search email templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border-gray-300 focus:border-[#3eaf99] focus:ring focus:ring-[#3eaf99] focus:ring-opacity-50"
            />
          </div>
        </div>

        <ListTable
          columns={columns}
          data={filteredTemplates}
          editPathPrefix="/edit-email-template"
          rowActions={(template) => (
            <div className="flex space-x-4">
              <button
                onClick={() => handleEditTemplate(template.id)}
                className="text-xs font-semibold uppercase text-gray-600 hover:text-gray-900"
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteTemplate(template.id)}
                className="text-xs font-semibold uppercase text-gray-600 hover:text-gray-900"
              >
                Delete
              </button>
            </div>
          )}
        />
      </div>

      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-white rounded-md shadow-lg p-6 max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">Delete email template?</DialogTitle>
          </DialogHeader>
          <p className="mt-2 text-sm text-gray-500">This action cannot be undone.</p>
          <DialogFooter className="mt-6 flex justify-end space-x-2">
            <Button
              variant="ghost"
              onClick={closeDeleteModal}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={confirmDelete}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
            >
              {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
